import React from "react";
import { useForm, useStep } from "react-hooks-helper";
import { Names } from "./stepForm/Names";
import { Address } from "./stepForm/Address";
import { Contact } from "./stepForm/Contact";
import { Review } from "./stepForm/Review";
import { Submit } from "./stepForm/Submit";
import { LoanOfficer } from "./stepForm/LoanOfficer";
import { WhatAreYouHopingToAchieve } from "./stepForm/WhatAreYouHopingToAchieve";
import { Build } from "./stepForm/build/Build";
import { Buy } from "./stepForm/buy/Buy";
import { WhatIsYourGoal } from "./stepForm/refinance/WhatIsYourGoal";
import HomeValue from "./stepForm/refinance/HomeValue";
import { RemainingBalance } from "./stepForm/refinance/RemainingBalance";
import { WhenAreYouLooking } from "./stepForm/refinance/WhenAreYouLooking";
import { DescribeYourCredit } from "./stepForm/refinance/DescribeYourCredit";
import { PrimaryBorrower } from "./stepForm/refinance/PrimaryBorrower";
import { HowCanWeReachYou } from "./stepForm/HowCanWeReachYou";
import { WhereAreYouLooking } from "./stepForm/buy/WhereAreYouLooking";
import { PropertyUse } from "./stepForm/buy/PropertyUse";
import { GeneralContractor } from "./stepForm/build/GeneralContractor";
// import { DownPayment } from "./stepForm/build/DownPayment";
import { EstimatedPurchasePrice } from "./stepForm/buy/EstimatedPurchasePrice";
import { DownPayment } from "./stepForm/buy/DownPayment";
import { ProjectBudget } from "./stepForm/build/ProjectBudget";
import { BuildDownPayment } from "./stepForm/build/BuildDownPayment";
import { OwnLotOfLand } from "./stepForm/build/OwnLotOfLand";
import { WhereIsThePropertyLocated } from "./stepForm/refinance/WhereIsThePropertyLocated";
import { WhereAreYouLookingToBuildTheHome } from "./stepForm/build/WhereAreYouLookingToBuildTheHome";
import { stepStyles } from "./stepForm/formStyles";

const FORM_STORAGE_KEY = "warpBuyFormData";
const STEP_STORAGE_KEY = "warpBuyFormStep";

const defaultData = {
  primaryBorrowerFirstName: "",
  primaryBorrowerLastName: "",
  loanReason: "",
  remainingBalance: "",
  whenAreYouLooking: "",
  describeYourCredit: "",
  phone: "",
  email: "",
  city: "",
  state: "",
  propertyUse: "",
  downPayment: "",
  purchasePrice: "",
};

const steps = [
  // start of buy
  { id: "whereAreYouLooking" },
  { id: "whenAreYouLooking" },
  { id: "estimatedPurchasePrice" },
  { id: "downPayment" },
  { id: "propertyUse" },
  { id: "describeYourCredit" },
  { id: "primaryBorrower" },
  { id: "howCanWeReachYou" },
];

const getSavedFormData = () => {
  try {
    const saved = localStorage.getItem(FORM_STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

const getSavedStep = () => {
  try {
    const saved = localStorage.getItem(STEP_STORAGE_KEY);
    const idx = parseInt(saved, 10);
    return !isNaN(idx) && idx >= 0 && idx < steps.length ? idx : 0;
  } catch {
    return 0;
  }
};

export const MultiStepForm = () => {
  const initialData = React.useMemo(() => ({ ...defaultData, ...getSavedFormData() }), []);
  const initialStep = React.useMemo(() => getSavedStep(), []);

  const [formData, setForm] = useForm(initialData);
  const { step, index, navigation } = useStep({
    steps,
    initialStep,
  });

  // Persist form data to localStorage
  React.useEffect(() => {
    try {
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(formData));
    } catch {}
  }, [formData]);

  // Persist current step to localStorage
  React.useEffect(() => {
    try {
      localStorage.setItem(STEP_STORAGE_KEY, String(index));
    } catch {}
  }, [index]);

  const handleStartOver = () => {
    try {
      localStorage.removeItem(FORM_STORAGE_KEY);
      localStorage.removeItem(STEP_STORAGE_KEY);
    } catch {}
    window.location.reload();
  };

  const props = { formData, setForm, navigation, useStep, step };

  const stepComponent = (() => {
    switch (step.id) {
      case "loanOfficer":
        return <LoanOfficer {...props} />;
      case "whereIsThePropertyLocated":
        return <WhereIsThePropertyLocated {...props} />;
      case "whereAreYouLookingToBuildTheHome":
        return <WhereAreYouLookingToBuildTheHome {...props} />;
      case "downPayment":
        return <DownPayment {...props} />;
      case "generalContractor":
        return <GeneralContractor {...props} />;
      case "propertyUse":
        return <PropertyUse {...props} />;
      case "whereAreYouLooking":
        return <WhereAreYouLooking {...props} />;
      case "estimatedPurchasePrice":
        return <EstimatedPurchasePrice {...props} />;
      case "whatAreYouHopingToAchieve":
        return <WhatAreYouHopingToAchieve {...props} />;
      case "names":
        return <Names {...props} />;
      case "WhatIsYourGoal":
        return <WhatIsYourGoal {...props} />;
      case "remainingBalance":
        return <RemainingBalance {...props} />;
      case "address":
        return <Address {...props} />;
      case "contact":
        return <Contact {...props} />;
      case "review":
        return <Review {...props} />;
      case "build":
        return <Build {...props} />;
      case "buy":
        return <Buy {...props} />;
      case "submit":
        return <Submit {...props} />;
      case "homeValue":
        return <HomeValue {...props} />;
      case "whenAreYouLooking":
        return <WhenAreYouLooking {...props} />;
      case "describeYourCredit":
        return <DescribeYourCredit {...props} />;
      case "primaryBorrower":
        return <PrimaryBorrower {...props} />;
      case "howCanWeReachYou":
        return <HowCanWeReachYou {...props} />;
      case "projectBudget":
        return <ProjectBudget {...props} />;
      case "buildDownPayment":
        return <BuildDownPayment {...props} />;
      case "ownLotOfLand":
        return <OwnLotOfLand {...props} />;
      default:
        return (
          <div className={stepStyles.page}>
            <div className={stepStyles.content}>
              <div className={stepStyles.panel}>
                <h1 className={stepStyles.title}>Warp Speed Mortgage</h1>
              </div>
            </div>
          </div>
        );
    }
  })();

  return (
    <>
      {stepComponent}
      <div className="pb-4 text-center">
        <button onClick={handleStartOver} className="text-xs text-gray-400 transition-colors hover:text-gray-400">
          Start over
        </button>
      </div>
    </>
  );
};
